import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { oktaAuthClient, refreshAccessToken, updateAuthState } from 'system/auth';
import Box from '@mui/material/Box';
import { injectIntl } from 'react-intl';
import { updateUserPolicy } from 'store/User/actions';
import messages from './messages';
import {
  ModalContainer,
  ModalTitle,
  CancelButton,
  SubmitButton,
  EffectiveDateTitle,
  NotAcceptingTermsTitle,
  Pager,
} from './styles';
import TermsAndConditionsViewer from '../TermsAndConditionsViewer/TermsAndConditionsViewer';

function ConsentManagementModal(props) {
  const { openModal = false, intl = {}, setUpdateUserPolicy, licenseURL, termsURL } = props;
  const { formatMessage } = intl;
  const oktaAuth = oktaAuthClient;
  const [modalIsOpen, setModalIsOpen] = useState(openModal);
  const [currentPage, setCurrentPage] = useState(0);
  const [userDeclined, setUserDeclined] = useState(false); // Track if user declined

  const documents = [termsURL, licenseURL];
  const effectiveDate = new Date();
  const formattedEffectiveDate = effectiveDate.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });

  const isLastPage = currentPage === documents.length - 1;

  const handleNextPage = () => {
    if (!isLastPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const logout = async () => {
    closeModal();
    await oktaAuth.signOut();
    window.location.href = '/login';
  };

  const decline = () => {
    setUserDeclined(true);
    logout();
  };

  const accepted = async () => {
    if (isLastPage) {
      setUpdateUserPolicy(effectiveDate);
      await refreshAccessToken();
      updateAuthState();
      closeModal();
    } else {
      handleNextPage();
    }
  };

  const modalTitle = (
    <ModalTitle variant="caption" data-testid="content-modalTitle">
      {formatMessage(messages.termsAndConditions)}
    </ModalTitle>
  );

  return (
    <ModalContainer
      open={modalIsOpen}
      title={modalTitle}
      data-testid="content-consentManagementModal"
    >
      <Box>
        <EffectiveDateTitle>
          {formatMessage(messages.effectiveDate)}
          {formattedEffectiveDate}
        </EffectiveDateTitle>
      </Box>
      <TermsAndConditionsViewer documents={documents} currentPage={currentPage} />

      <Pager>
        <li>
          <CancelButton type="button" onClick={decline}>
            {formatMessage(messages.decline)}
          </CancelButton>
        </li>
        <li>
          <SubmitButton onClick={accepted} disabled={userDeclined}>
            {isLastPage ? formatMessage(messages.acceptTerms) : formatMessage(messages.reviewTerms)}
          </SubmitButton>
        </li>
      </Pager>

      {userDeclined && (
        <NotAcceptingTermsTitle>{formatMessage(messages.notAcceptingTerms)}</NotAcceptingTermsTitle>
      )}
    </ModalContainer>
  );
}

ConsentManagementModal.propTypes = {
  intl: PropTypes.any.isRequired,
  openModal: PropTypes.bool,
  licenseURL: PropTypes.string,
  termsURL: PropTypes.string,
  setUpdateUserPolicy: PropTypes.func,
};

/**
 * mapDispatchToProps
 * @param {Function} setUpdateUserPolicy call updateUserPolicy action and update user terms and conditions
 */
const mapDispatchToProps = (dispatch) => ({
  setUpdateUserPolicy: (date) => dispatch(updateUserPolicy(date)),
});

export default connect(null, mapDispatchToProps)(injectIntl(ConsentManagementModal));
